// AppfolioSyncChip — the occupancy-document sync status, shown wherever a document
// appears (Condition Reports rows + detail, Requests dashboard groups). A failed or
// stuck upload gets a staff "Retry" (re-arms a fresh attempt — never a duplicate).

import React, { useState } from 'react';
import { type AppfolioSyncDisplay, retryOccupancyUpload } from '@/lib/appfolioSync';

export function AppfolioSyncChip({
  sync,
  onRetried,
}: {
  sync: AppfolioSyncDisplay;
  /** Called after a successful retry so the surface can refresh. */
  onRetried?: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const title =
    sync.error ?? (sync.attachmentId ? `AppFolio attachment ${sync.attachmentId}` : undefined);

  const retry = async (e: React.MouseEvent) => {
    e.stopPropagation(); // don't trigger a row click
    setBusy(true);
    setErr(null);
    try {
      await retryOccupancyUpload(sync.docKey);
      onRetried?.();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span className="pill" data-tone={sync.tone} data-size="sm" title={title}>
        AppFolio: {sync.label}
        {sync.versionNum > 1 ? ` (v${sync.versionNum})` : ''}
      </span>
      {sync.retryable && (
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
          disabled={busy}
          onClick={(e) => void retry(e)}
        >
          {busy ? 'Retrying…' : 'Retry'}
        </button>
      )}
      {err && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--danger, #b00)' }}>{err}</span>}
    </span>
  );
}
